import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  padding: 20px;
  background: transparent;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 20px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const Actions = styled.div`
  ${actionContainerStyles};
  overflow: hidden;

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: ${breakpoints.tablet}px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    border-bottom: 1px solid #ccc;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 10px;
  }
`;

const LinkButton = styled.a`
  display: inline-block;
  padding: 5px 10px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 5px;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: ${LINK_HOVER_COLOR};
  }
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 20px;
  border-radius: 5px;
  @media (max-width: ${breakpoints.phone}px) {
    width: 300px;
  }
`;

const Field = styled.div`
  margin-bottom: 15px;

  label {
    display: block;
    margin-bottom: 5px;
    color: ${({ colors }) => colors.labelText};
    font-size: 14px;
  }

  input,
  select {
    width: 95%;
    padding: 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
    font-size: 14px;
  }
`;

const Submit = styled.input`
  padding: 8px 16px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

export default function AdminsContent({ colors }) {
  const [view, setView] = useState("list");
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const adminRoles = useSelector((state) => state.auth.admin_roles || []);
  console.log(adminRoles)
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState(
    adminRoles.length ? adminRoles[0].role : ""
  );

  // confirmation modals
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [targetUserId, setTargetUserId] = useState(null);

  useEffect(() => {
    // fetch the list
    (async () => {
      try {
        const resp = await API.getAdmins({});
        if (resp?.users) setAdmins(resp.users);
      } catch (err) {
        console.error("Failed to load admins:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---- Revoke flow ----
  const onRevokeClick = (user_id) => {
    setTargetUserId(user_id);
    setModalTitle("Revoke Admin");
    setModalMessage("Are you sure you want to revoke this admin?");
    setConfirmOpen(true);
  };

  const handleConfirmRevoke = async () => {
    setConfirmOpen(false);
    setModalStatus("Success");
    try {
      const resp = await API.revokeAdmin({ user_id: targetUserId });
      setAdmins((prev) =>
        prev.filter((a) => a.user_id !== targetUserId)
      );
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Success");
      setModalMessage(resp.message || (status === "Failed" ? "Failed to revoke admin." : "Admin revoked successfully."));
      setModalStatus(status);
    } catch (err) {
      console.error("Revoke failed:", err);
      setModalTitle("Error");
      setModalMessage("Failed to revoke admin. Please try again.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  // ---- Add flow ----
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setModalStatus("Success");
    try {
      const resp = await API.addAdmin({
        first_name: newFirstName,
        last_name: newLastName,
        email: newEmail,
        role: newRole,
      });
      // re-fetch list
      const list = await API.getAdmins({});
      setAdmins(list.users || []);
      setNewFirstName("");
      setNewLastName("");
      setNewEmail("");
      setView("list");
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Success");
      setModalMessage(resp.message || (status === "Failed" ? "Failed to add admin." : "Admin added successfully."));
      setModalStatus(status);
    } catch (err) {
      console.error("Add admin failed:", err);
      setModalTitle("Error");
      setModalMessage("Failed to add admin. Please try again.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  return (
    <Container>
      {/* confirm revoke */}
      <ConfirmationModal
        show={confirmOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmRevoke}
      />
      {/* result of revoke/add */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      {/* Actions pane */}
      {view === "list" && (
        <ResponsiveActions
          colors={colors}
          renderActions={() => (
            <Actions colors={colors}>
              <h3>Actions</h3>
              <ul>
                <li>
                  <a onClick={() => setView("add")} href="#">
                    Add
                  </a>
                </li>
              </ul>
            </Actions>
          )}
        />
      )}

      <View>
        {view === "list" ? (
          loading ? (
            <p>Loading admins…</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-mail</th>
                  <th>Role</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.user_id}>
                    <td>{a.name}</td>
                    <td>{a.email}</td>
                    <td>{a.role_desc}</td>
                    <td>
                      <LinkButton
                        colors={colors}
                        onClick={() => onRevokeClick(a.user_id)}
                      >
                        Revoke
                      </LinkButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )
        ) : (
          <Form colors={colors} onSubmit={handleAddSubmit}>
            <fieldset>
              <legend style={{ color: colors.labelText }}>
                Add New Admin
              </legend>
              <Field colors={colors}>
                <label htmlFor="isafeadminFirstName">First Name</label>
                <input
                  id="isafeadminFirstName"
                  type="text"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label htmlFor="isafeadminLastName">Last Name</label>
                <input
                  id="isafeadminLastName"
                  type="text"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label htmlFor="isafeadminEmail">E-mail Address</label>
                <input
                  id="isafeadminEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label htmlFor="isafeadminRole">Role</label>
                <select
                  id="isafeadminRole"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  required
                >
                  {adminRoles.map(({ role, role_title }) => (
                    <option key={role} value={role}>
                      {role_title}
                    </option>
                  ))}
                </select>
              </Field>
              <Submit colors={colors} type="submit" value="Submit" />
              <a
                className="cancel"
                onClick={() => setView("list")}
                style={{
                  marginLeft: "1em",
                  fontSize: "0.95em",
                  color: colors.labelText,
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </a>
            </fieldset>
          </Form>
        )}
      </View>
    </Container>
  );
}
